import React, { useEffect, useMemo, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { ExternalLink, X, Compass } from 'lucide-react';
import { getListingPresentation } from '../../utils/hostListings';
import { Badge, Card } from './AdminPanelPrimitives';
import { ADMIN_PALETTE, STATUS_PALETTE, FILTER_TO_STATUS, MapLegend } from './adminDesignSystem';

const MAP_STYLE_URL = 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json';
const ADMIN_RADIUS_KM = 20;

const RADIUS_SOURCE_ID = 'admin-radius-source';
const RADIUS_FILL_LAYER_ID = 'admin-radius-fill';
const RADIUS_LINE_LAYER_ID = 'admin-radius-line';

export const parseCoordinatePair = (value) => {
  if (!value || typeof value !== 'string' || !value.includes(',')) {
    return { latitude: null, longitude: null };
  }
  const [lat, lng] = value.split(',').map((entry) => Number(entry.trim()));
  if (Number.isNaN(lat) || Number.isNaN(lng)) {
    return { latitude: null, longitude: null };
  }
  return { latitude: lat, longitude: lng };
};

export const toggleSelectableFilters = (selectedFilters, nextFilter) => {
  if (nextFilter === 'all') {
    return ['all'];
  }

  const current = Array.isArray(selectedFilters) && selectedFilters.length ? selectedFilters.filter((entry) => entry !== 'all') : [];
  const hasFilter = current.includes(nextFilter);
  const next = hasFilter ? current.filter((entry) => entry !== nextFilter) : [...current, nextFilter];
  return next.length ? next : ['all'];
};

const buildEmptyFeatureCollection = () => ({ type: 'FeatureCollection', features: [] });

const buildRadiusFeatureCollection = (latitude, longitude, radiusKm = ADMIN_RADIUS_KM, steps = 72) => {
  const latitudeRadians = (latitude * Math.PI) / 180;
  const features = [];
  const coordinates = [];

  for (let step = 0; step <= steps; step += 1) {
    const angle = (step / steps) * Math.PI * 2;
    const latOffset = (radiusKm / 111.32) * Math.sin(angle);
    const lngOffset = (radiusKm / (111.32 * Math.max(Math.cos(latitudeRadians), 0.01))) * Math.cos(angle);
    coordinates.push([longitude + lngOffset, latitude + latOffset]);
  }

  features.push({
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [coordinates],
    },
    properties: {},
  });

  return { type: 'FeatureCollection', features };
};

// True if the 20km radius around (lat, lng) is fully inside the
// current map viewport (with a small padding margin). Used to skip
// the easeTo recenter when the radius is already visible — moving the
// map every click felt jumpy and made the admin lose visual context.
const radiusFitsInViewport = (map, latitude, longitude, radiusKm = ADMIN_RADIUS_KM, paddingPx = 32) => {
  if (!map) return false;
  const latitudeRadians = (latitude * Math.PI) / 180;
  const latOffset = radiusKm / 111.32;
  const lngOffset = radiusKm / (111.32 * Math.max(Math.cos(latitudeRadians), 0.01));
  const north = latitude + latOffset;
  const south = latitude - latOffset;
  const east = longitude + lngOffset;
  const west = longitude - lngOffset;
  // Project each corner to screen pixels and check it sits inside the
  // visible canvas with a small inset padding so the radius doesn't
  // hug the edge.
  const corners = [
    map.project([west, north]),
    map.project([east, north]),
    map.project([east, south]),
    map.project([west, south]),
  ];
  const canvas = map.getCanvas();
  const minX = paddingPx;
  const minY = paddingPx;
  const maxX = canvas.clientWidth - paddingPx;
  const maxY = canvas.clientHeight - paddingPx;
  return corners.every((point) => point.x >= minX && point.x <= maxX && point.y >= minY && point.y <= maxY);
};

const ensureRadiusLayers = (map) => {
  if (!map.getSource(RADIUS_SOURCE_ID)) {
    map.addSource(RADIUS_SOURCE_ID, {
      type: 'geojson',
      data: buildEmptyFeatureCollection(),
    });
  }
  if (!map.getLayer(RADIUS_FILL_LAYER_ID)) {
    map.addLayer({
      id: RADIUS_FILL_LAYER_ID,
      type: 'fill',
      source: RADIUS_SOURCE_ID,
      paint: {
        'fill-color': '#703FF6',
        'fill-opacity': 0.18,
      },
    });
  }
  if (!map.getLayer(RADIUS_LINE_LAYER_ID)) {
    map.addLayer({
      id: RADIUS_LINE_LAYER_ID,
      type: 'line',
      source: RADIUS_SOURCE_ID,
      paint: {
        'line-color': '#703FF6',
        'line-width': 3,
        'line-opacity': 0.7,
      },
    });
  }
};

export const buildListingMapItem = (application) => {
  const latitude = Number(application?.listing_latitude);
  const longitude = Number(application?.listing_longitude);
  if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
    return null;
  }

  const listingMeta = getListingPresentation({
    title: application?.listing_title,
    address: application?.listing_location_label,
  });

  return {
    id: `listing-${application.id}`,
    entityType: 'listing',
    title: listingMeta.displayName || 'Host listing',
    subtitle: listingMeta.displayAddress || 'Approximate listing location',
    badgeText: application.status === 'approved' ? 'Active listing' : 'Early listing',
    badgeColor: application.status === 'approved' ? 'green' : 'orange',
    statusTone: application.status === 'approved' ? 'active' : 'early',
    markerColor: application.status === 'approved' ? STATUS_PALETTE.active.solid : STATUS_PALETTE.early.solid,
    latitude,
    longitude,
    ownerName: application.user?.name || application.user?.email || 'Host',
    ownerEmail: application.user?.email || '',
    accountId: application.user?.customer_id || '—',
    externalUrl: application.airbnb_url || '',
    statusKey: application.status,
  };
};

export const buildVenueMapItem = (application) => {
  const pair = parseCoordinatePair(application?.coordinates || '');
  const latitude = pair.latitude ?? Number(application?.latitude);
  const longitude = pair.longitude ?? Number(application?.longitude);
  if (Number.isNaN(latitude) || Number.isNaN(longitude) || latitude == null || longitude == null) {
    return null;
  }

  const status = application?.status === 'approved' ? 'on_hold' : application?.status || 'venue';
  const venueTone = status === 'live' ? 'venue' : status === 'on_hold' ? 'hold' : 'pending';
  return {
    id: `venue-${application.id}`,
    entityType: 'venue',
    title: application.restaurant_name || application.map_name || 'Venue',
    subtitle: [application.city, application.country].filter(Boolean).join(', ') || 'Approximate venue location',
    badgeText: status === 'live' ? 'Live venue' : status === 'on_hold' ? 'On hold venue' : 'Pending venue',
    badgeColor: status === 'live' ? 'blue' : status === 'on_hold' ? 'orange' : 'blue',
    statusTone: venueTone,
    markerColor: STATUS_PALETTE[venueTone].solid,
    latitude,
    longitude,
    ownerName: application.user?.name || `${application.first_name || ''} ${application.last_name || ''}`.trim() || 'Venue owner',
    ownerEmail: application.user?.email || application.contact_email || '',
    accountId: application.user?.customer_id || '—',
    externalUrl: '',
    statusKey: status,
  };
};

const markerElement = (color, active) => {
  // MapLibre positions the marker via `transform: translate(x, y)` on this
  // outer button. Writing to `element.style.transform` directly clobbers
  // that translate and the marker snaps to the top-left corner of the
  // map on hover. Wrap the visual in an inner span and apply the hover
  // scale to the SPAN instead so MapLibre's positioning is preserved.
  const element = document.createElement('button');
  element.type = 'button';
  element.setAttribute('aria-label', 'Map marker');
  element.style.padding = '0';
  element.style.border = 'none';
  element.style.background = 'transparent';
  element.style.cursor = 'pointer';

  const dot = document.createElement('span');
  dot.style.display = 'block';
  dot.style.width = active ? '22px' : '16px';
  dot.style.height = active ? '22px' : '16px';
  dot.style.borderRadius = '50%';
  dot.style.border = '2px solid #FFFFFF';
  dot.style.background = color;
  dot.style.boxShadow = active ? `0 0 0 7px ${color}30, 0 8px 18px rgba(17,17,17,0.18)` : '0 6px 14px rgba(17,17,17,0.14)';
  dot.style.transition = 'transform 140ms ease, box-shadow 140ms ease';
  dot.style.transformOrigin = 'center';
  element.appendChild(dot);

  element.addEventListener('mouseenter', () => {
    dot.style.transform = 'scale(1.18)';
  });
  element.addEventListener('mouseleave', () => {
    dot.style.transform = 'scale(1)';
  });
  return element;
};

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const buildPopupHtml = (item) => {
  const palette = STATUS_PALETTE[item.statusTone] || STATUS_PALETTE.neutral;
  const ownerLine = item.ownerName ? `<div style="font-size:12px;color:#3B404B;margin-top:8px;font-weight:600;">${escapeHtml(item.ownerName)}</div>` : '';
  const emailLine = item.ownerEmail ? `<div style="font-size:11px;color:#6B7280;margin-top:2px;">${escapeHtml(item.ownerEmail)}</div>` : '';
  return `
    <div style="font-family:'Manrope',sans-serif;min-width:200px;max-width:260px;">
      <div style="display:inline-flex;align-items:center;gap:6px;padding:3px 9px;border-radius:9999px;background:${palette.soft};color:${palette.text};font-size:10.5px;font-weight:800;letter-spacing:0.4px;text-transform:uppercase;border:1px solid ${palette.solid}33;">
        <span style="width:7px;height:7px;border-radius:9999px;background:${palette.solid};"></span>
        ${escapeHtml(item.badgeText || palette.label)}
      </div>
      <div style="margin-top:8px;font-size:14px;font-weight:700;color:#0F1115;line-height:1.25;">${escapeHtml(item.title)}</div>
      <div style="font-size:12px;color:#6B7280;margin-top:3px;">${escapeHtml(item.subtitle || '')}</div>
      ${ownerLine}${emailLine}
      <button type="button" data-popup-action="open-card" data-testid="admin-map-popup-open-card" style="margin-top:12px;display:inline-flex;align-items:center;gap:6px;padding:7px 12px;border-radius:10px;border:1px solid ${palette.solid}55;background:${palette.solid};color:#FFFFFF;font-size:12px;font-weight:700;cursor:pointer;font-family:inherit;">
        Open card
        <span aria-hidden="true" style="font-size:13px;line-height:1;">→</span>
      </button>
    </div>
  `;
};

const containerStyle = {
  background: '#FFF',
  borderRadius: 18,
  padding: 18,
  border: '1px solid #E7E7E7',
  boxShadow: '0 10px 28px rgba(17,17,17,0.04)',
};

export const AdminGeoMapExplorer = ({
  dataTestId,
  description,
  emptyCopy,
  enableHoverRadius = false,
  filterOptions = [],
  items,
  onClose,
  onFilterToggle,
  onOpenDetail,
  onSelect,
  onToggleRadius,
  radiusEnabled = false,
  selectedId,
  selectedFilters = ['all'],
  title,
}) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const popupsRef = useRef([]);
  const itemCardRefs = useRef({});
  const lastCenteredIdRef = useRef(null);
  // Tracks the items-array signature that's currently rendered on the
  // map. We use this to decide whether to refit bounds — otherwise the
  // marker rebuild effect (which also runs when selectedId changes)
  // would call fitBounds on every pin click and zoom the map all the
  // way back out. See the unzoom-on-click bug report.
  const lastItemsSignatureRef = useRef('');
  const [hoveredRadiusItemId, setHoveredRadiusItemId] = useState('');
  // Drag-the-radius state: when the admin presses & holds the 20km
  // radius circle we let them drag JUST the circle around the map
  // (without moving the venue pin or writing anything to the backend).
  // On release we snap the circle back to the pin instantly.
  const [dragCenter, setDragCenter] = useState(null);
  const isDraggingRadiusRef = useRef(false);
  const hoveredRadiusItemRef = useRef(null);

  const selectedItem = useMemo(() => items.find((item) => item.id === selectedId) || items[0] || null, [items, selectedId]);
  const hoveredRadiusItem = useMemo(() => items.find((item) => item.id === hoveredRadiusItemId) || null, [hoveredRadiusItemId, items]);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) {
      return undefined;
    }

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: MAP_STYLE_URL,
      center: [10, 20],
      zoom: 1.6,
      attributionControl: false,
    });

    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    map.on('load', () => {
      ensureRadiusLayers(map);
    });
    // Clicking on the map background (NOT on a pin) should dismiss any
    // stuck popups. Without this, popups linger if a hover-leave event
    // is missed (browser focus loss, fast pin-to-pin movement, etc.).
    map.on('click', () => {
      popupsRef.current.forEach((popup) => {
        try { popup.remove(); } catch { /* already removed */ }
      });
    });

    // ----------------------------------------------------------------
    // Press-and-hold to drag the 20km radius circle
    // ----------------------------------------------------------------
    // Admins want to test hypothetical coverage zones without moving
    // the venue pin or writing anything to the backend. We let them
    // grab the radius circle, drag it anywhere on the map, then snap
    // it back to the venue pin instantly on release.
    const bindRadiusDrag = () => {
      const startDrag = (event) => {
        if (!hoveredRadiusItemRef.current) return;
        event.preventDefault();
        isDraggingRadiusRef.current = true;
        map.dragPan.disable();
        map.getCanvas().style.cursor = 'grabbing';
        setDragCenter({ lat: event.lngLat.lat, lng: event.lngLat.lng });
      };
      const moveDrag = (event) => {
        if (!isDraggingRadiusRef.current) return;
        setDragCenter({ lat: event.lngLat.lat, lng: event.lngLat.lng });
      };
      const endDrag = () => {
        if (!isDraggingRadiusRef.current) return;
        isDraggingRadiusRef.current = false;
        map.dragPan.enable();
        map.getCanvas().style.cursor = '';
        // Snap back to the venue pin — clear the drag center so the
        // radius source effect re-renders the circle at the hovered/
        // selected item's coordinates.
        setDragCenter(null);
      };

      [RADIUS_FILL_LAYER_ID, RADIUS_LINE_LAYER_ID].forEach((layerId) => {
        map.on('mousedown', layerId, startDrag);
        map.on('touchstart', layerId, startDrag);
        map.on('mouseenter', layerId, () => {
          if (!isDraggingRadiusRef.current && hoveredRadiusItemRef.current) {
            map.getCanvas().style.cursor = 'grab';
          }
        });
        map.on('mouseleave', layerId, () => {
          if (!isDraggingRadiusRef.current) {
            map.getCanvas().style.cursor = '';
          }
        });
      });
      map.on('mousemove', moveDrag);
      map.on('touchmove', moveDrag);
      map.on('mouseup', endDrag);
      map.on('touchend', endDrag);
      // Safety: if the pointer leaves the canvas mid-drag, end it.
      map.getCanvas().addEventListener('mouseleave', endDrag);
    };
    map.on('load', bindRadiusDrag);

    mapRef.current = map;

    return () => {
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
      popupsRef.current.forEach((popup) => {
        try { popup.remove(); } catch { /* already removed */ }
      });
      popupsRef.current = [];
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current) {
      return;
    }

    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];
    popupsRef.current.forEach((popup) => {
      try { popup.remove(); } catch { /* already removed */ }
    });
    popupsRef.current = [];

    if (!items.length) {
      lastItemsSignatureRef.current = '';
      return;
    }

    // Decide whether to refit bounds. We refit only when the set of
    // items actually changes (filter toggled, fresh data fetched), NOT
    // when only `selectedId` changes — otherwise tapping a pin zooms
    // the map all the way back out.
    const itemsSignature = items.map((item) => item.id).join('|');
    const itemsChanged = itemsSignature !== lastItemsSignatureRef.current;
    if (itemsChanged) {
      lastItemsSignatureRef.current = itemsSignature;
    }

    const bounds = new maplibregl.LngLatBounds();
    items.forEach((item) => {
      bounds.extend([item.longitude, item.latitude]);
      const element = markerElement(item.markerColor, item.id === selectedId);
      // Anchor 'bottom' so the popup always sits above the pin and
      // doesn't reflow when the pin is near a map edge. `closeButton`
      // is enabled so admins can dismiss the bubble without losing the
      // 20km radius highlight underneath.
      const popup = new maplibregl.Popup({
        closeButton: true,
        closeOnClick: false,
        offset: 14,
        anchor: 'bottom',
        maxWidth: '260px',
        className: 'admin-map-popup',
      }).setHTML(buildPopupHtml(item));
      popupsRef.current.push(popup);
      const closeAllPopups = () => {
        popupsRef.current.forEach((other) => {
          try { other.remove(); } catch { /* already removed */ }
        });
      };
      // Hover only updates the 20km radius highlight — NOT the popup.
      // Previously the popup opened on hover and overlapped the radius
      // circle, hiding the very area the admin was trying to inspect.
      element.addEventListener('mouseenter', () => {
        if (radiusEnabled && enableHoverRadius) {
          setHoveredRadiusItemId(item.id);
        }
      });
      element.addEventListener('mouseleave', () => {
        // Keep the radius pinned to the currently-hovered item while a
        // drag is in progress — otherwise mouseleave would clear it the
        // moment the cursor enters the radius layer.
        if (isDraggingRadiusRef.current) return;
        if (radiusEnabled && enableHoverRadius) {
          setHoveredRadiusItemId('');
        }
      });
      // Click selects the pin (which draws the persistent radius) AND
      // opens the popup, so the admin gets all the venue details on
      // demand without losing the radius context.
      element.addEventListener('click', (event) => {
        event.stopPropagation();
        closeAllPopups();
        onSelect(item.id);
        popup.setLngLat([item.longitude, item.latitude]).addTo(mapRef.current);
        // Wire the in-popup "Open card" button: scroll the matching
        // left-rail card into view and add a brief highlight pulse so
        // admins can spot it instantly. Bind after the popup is in the
        // DOM because MapLibre lazy-renders the popup content.
        requestAnimationFrame(() => {
          const popupEl = popup.getElement();
          const openCardBtn = popupEl?.querySelector('[data-popup-action="open-card"]');
          if (openCardBtn) {
            openCardBtn.addEventListener('click', (btnEvent) => {
              btnEvent.stopPropagation();
              const cardEl = itemCardRefs.current[item.id];
              if (cardEl && typeof cardEl.scrollIntoView === 'function') {
                cardEl.scrollIntoView({ block: 'center', behavior: 'smooth' });
                cardEl.classList.add('admin-map-card-pulse');
                window.setTimeout(() => {
                  try { cardEl.classList.remove('admin-map-card-pulse'); } catch { /* unmounted */ }
                }, 1600);
              }
              try { popup.remove(); } catch { /* already removed */ }
            }, { once: true });
          }
        });
      });
      const marker = new maplibregl.Marker({ element, anchor: 'center' })
        .setLngLat([item.longitude, item.latitude])
        .addTo(mapRef.current);
      markersRef.current.push(marker);
    });

    if (items.length === 1) {
      if (itemsChanged) {
        mapRef.current.easeTo({ center: [items[0].longitude, items[0].latitude], zoom: 5.2, duration: 600 });
      }
      return;
    }

    if (itemsChanged) {
      mapRef.current.fitBounds(bounds, { padding: 60, duration: 650, maxZoom: 5.8 });
    }
  }, [items, onSelect, selectedId]);

  useEffect(() => {
    if (!mapRef.current || !selectedItem) {
      return;
    }
    // Skip recenter when only the object reference changed but the id is
    // the same — prevents the easeTo from racing with the initial fitBounds
    // animation on first mount (which made the pin appear in the corner).
    if (lastCenteredIdRef.current === selectedItem.id) {
      return;
    }
    lastCenteredIdRef.current = selectedItem.id;
    // Only recenter when the 20km radius wouldn't otherwise be fully
    // visible. Tapping a pin whose radius already sits inside the
    // current viewport now keeps the map perfectly still — admins were
    // complaining the map "jumps too much" on every pin click.
    if (!radiusFitsInViewport(mapRef.current, selectedItem.latitude, selectedItem.longitude)) {
      mapRef.current.easeTo({ center: [selectedItem.longitude, selectedItem.latitude], zoom: Math.max(mapRef.current.getZoom(), 4.2), duration: 500 });
    }

    // "Jump from pin to card": when the selection changes (either via a
    // pin click on the map or a card click on the rail), make sure the
    // matching card scrolls into view in the left rail.
    const cardEl = itemCardRefs.current[selectedItem.id];
    if (cardEl && typeof cardEl.scrollIntoView === 'function') {
      cardEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedItem]);

  // Keep the ref in sync with state so the MapLibre event handlers
  // (which are bound once at mount) can read the latest hovered item.
  useEffect(() => {
    hoveredRadiusItemRef.current = hoveredRadiusItem;
  }, [hoveredRadiusItem]);

  useEffect(() => {
    if (!mapRef.current || !mapRef.current.isStyleLoaded()) {
      return;
    }

    ensureRadiusLayers(mapRef.current);
    const source = mapRef.current.getSource(RADIUS_SOURCE_ID);
    if (!source) {
      return;
    }

    // Decide where the radius circle sits this frame. When the admin
    // is press-and-holding the circle, follow the cursor. Otherwise
    // fall back to the hovered item (the original behavior).
    let centerLat = null;
    let centerLng = null;
    if (dragCenter) {
      centerLat = dragCenter.lat;
      centerLng = dragCenter.lng;
    } else if (radiusEnabled && enableHoverRadius && hoveredRadiusItem) {
      centerLat = hoveredRadiusItem.latitude;
      centerLng = hoveredRadiusItem.longitude;
    }

    if (centerLat == null || centerLng == null) {
      source.setData(buildEmptyFeatureCollection());
      return;
    }

    source.setData(buildRadiusFeatureCollection(centerLat, centerLng, ADMIN_RADIUS_KM));
    // Intentionally no auto-pan here. Per admin feedback, hovering a
    // pin or revealing the radius should NEVER move the map — only an
    // explicit pin CLICK is allowed to trigger a recenter (handled by
    // the selectedItem effect above). Keeping the map perfectly still
    // during hover/drag avoids "the map keeps shifting under me" pain.
  }, [enableHoverRadius, hoveredRadiusItem, radiusEnabled, dragCenter]);

  return (
    <div data-testid={dataTestId} style={containerStyle}>
      <style>{`
        @keyframes admin-map-card-pulse-anim {
          0%   { box-shadow: 0 0 0 0 rgba(112, 63, 246, 0.55), 0 10px 28px rgba(112, 63, 246, 0.18); }
          70%  { box-shadow: 0 0 0 14px rgba(112, 63, 246, 0),    0 10px 28px rgba(112, 63, 246, 0.22); }
          100% { box-shadow: 0 0 0 0 rgba(112, 63, 246, 0),       0 10px 28px rgba(112, 63, 246, 0.10); }
        }
        .admin-map-card-pulse {
          animation: admin-map-card-pulse-anim 1.4s ease-out 1;
        }
        .admin-map-popup .maplibregl-popup-close-button {
          color: #6B7280;
          font-size: 18px;
          padding: 2px 6px;
        }
        .admin-map-popup .maplibregl-popup-close-button:hover {
          color: #0F1115;
          background: rgba(15, 17, 21, 0.05);
        }
      `}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div>
          <p style={{ margin: 0, fontFamily: "'Outfit', sans-serif", fontSize: 22, fontWeight: 700 }}>{title}</p>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#666', maxWidth: 620 }}>{description}</p>
        </div>
        {onClose ? (
          <button type="button" onClick={onClose} data-testid={`${dataTestId}-close-button`} style={{ border: '1px solid #E7E7E7', borderRadius: 12, background: '#FFF', cursor: 'pointer', padding: '8px 10px', display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 700 }}>
            <X size={14} /> Close
          </button>
        ) : null}
      </div>

      {filterOptions.length ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14, alignItems: 'center' }}>
          {filterOptions.map((option) => {
            const toneKey = FILTER_TO_STATUS[option.id] || 'neutral';
            const palette = STATUS_PALETTE[toneKey];
            const isActive = selectedFilters.includes(option.id);
            const showAccent = option.id !== 'all';
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => onFilterToggle(option.id)}
                data-testid={`${dataTestId}-filter-${option.id}`}
                style={{
                  padding: '8px 12px',
                  borderRadius: 9999,
                  border: `1px solid ${isActive ? palette.solid : ADMIN_PALETTE.border}`,
                  background: isActive ? palette.solid : ADMIN_PALETTE.surface,
                  color: isActive ? '#FFFFFF' : ADMIN_PALETTE.inkSoft,
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: 12.5,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  transition: 'background 120ms ease, color 120ms ease, border-color 120ms ease',
                }}
              >
                {showAccent ? (
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 9999,
                      background: isActive ? '#FFFFFF' : palette.solid,
                      display: 'inline-block',
                    }}
                  />
                ) : null}
                {option.label}
              </button>
            );
          })}
        </div>
      ) : null}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 12, alignItems: 'center', justifyContent: 'space-between' }}>
        <MapLegend
          entries={[
            { id: 'active-listing', tone: 'active', label: 'Active listing' },
            { id: 'early-listing', tone: 'early', label: 'Early listing' },
            { id: 'live-venue', tone: 'venue', label: 'Live venue' },
            { id: 'hold-venue', tone: 'hold', label: 'On hold venue' },
            { id: 'pending-venue', tone: 'pending', label: 'Pending venue' },
          ]}
        />
        {enableHoverRadius ? (
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 12px',
              borderRadius: 12,
              background: radiusEnabled ? `${STATUS_PALETTE.pending.solid}14` : ADMIN_PALETTE.surfaceMuted,
              border: `1px solid ${radiusEnabled ? STATUS_PALETTE.pending.solid : ADMIN_PALETTE.border}`,
              fontSize: 12.5,
              fontWeight: 700,
              color: radiusEnabled ? STATUS_PALETTE.pending.text : ADMIN_PALETTE.inkSoft,
              cursor: 'pointer',
            }}
          >
            <Compass size={14} />
            <span>Show 20km radius on hover · drag to test</span>
            <input
              type="checkbox"
              checked={radiusEnabled}
              onChange={onToggleRadius}
              data-testid={`${dataTestId}-radius-toggle`}
              style={{ accentColor: STATUS_PALETTE.pending.solid, width: 16, height: 16, cursor: 'pointer' }}
            />
          </label>
        ) : null}
      </div>

      <div style={{ display: 'flex', gap: 18, alignItems: 'stretch', marginTop: 18, flexWrap: 'wrap' }}>
        <div style={{ flex: '0 0 360px', minWidth: 320, display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 560, overflowY: 'auto', paddingRight: 4 }}>
          {selectedItem ? (
            <Card style={{ border: `1px solid ${selectedItem.markerColor}33`, boxShadow: `0 10px 28px ${selectedItem.markerColor}14` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                <div>
                  <p style={{ margin: 0, fontFamily: "'Outfit', sans-serif", fontSize: 19, fontWeight: 700 }}>{selectedItem.title}</p>
                  <p style={{ margin: '5px 0 0', fontSize: 13, color: '#666' }}>{selectedItem.subtitle}</p>
                </div>
                <Badge text={selectedItem.badgeText} color={selectedItem.badgeColor} />
              </div>
              <p style={{ margin: '12px 0 0', fontSize: 13, color: '#444' }}>{selectedItem.ownerName}</p>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#666' }}>{selectedItem.ownerEmail}</p>
              <p style={{ margin: '8px 0 0', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: '#111' }}>Account ID: {selectedItem.accountId}</p>
              {radiusEnabled && selectedItem.entityType === 'listing' ? (
                <p style={{ margin: '10px 0 0', fontSize: 13, color: '#444', lineHeight: 1.3 }}>
                  {selectedItem.nearestVenueDistanceKm != null
                    ? `Nearest active venue: ${selectedItem.nearestVenueTitle} · ${selectedItem.nearestVenueDistanceKm}km away`
                    : 'No active venue is mapped closely enough to compare yet.'}
                </p>
              ) : null}
              {selectedItem.externalUrl ? (
                <a href={selectedItem.externalUrl} target="_blank" rel="noopener noreferrer" style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 6, color: '#2F79EA', fontSize: 13, textDecoration: 'none', width: 'fit-content' }}>
                  <ExternalLink size={12} /> Open source
                </a>
              ) : null}
              {onOpenDetail ? (
                <button
                  type="button"
                  onClick={() => onOpenDetail(selectedItem)}
                  data-testid={`${dataTestId}-open-detail`}
                  style={{
                    marginTop: 16,
                    alignSelf: 'flex-start',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '9px 14px',
                    borderRadius: 10,
                    border: 'none',
                    background: '#0F1115',
                    color: '#FFFFFF',
                    cursor: 'pointer',
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  Open full details →
                </button>
              ) : null}
            </Card>
          ) : (
            <Card><p style={{ margin: 0, color: '#666' }}>{emptyCopy}</p></Card>
          )}

          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              ref={(el) => { if (el) itemCardRefs.current[item.id] = el; }}
              onClick={() => onSelect(item.id)}
              onDoubleClick={() => onOpenDetail && onOpenDetail(item)}
              data-testid={`${dataTestId}-item-${item.id}`}
              style={{ textAlign: 'left', ...containerStyle, border: item.id === selectedId ? `1px solid ${item.markerColor}55` : '1px solid #E7E7E7', cursor: 'pointer', boxShadow: item.id === selectedId ? `0 10px 28px ${item.markerColor}12` : containerStyle.boxShadow }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                <div>
                  <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#111' }}>{item.title}</p>
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: '#666' }}>{item.subtitle}</p>
                </div>
                <Badge text={item.badgeText} color={item.badgeColor} />
              </div>
            </button>
          ))}
          {!items.length ? <Card><p style={{ margin: 0, color: '#666' }}>{emptyCopy}</p></Card> : null}
        </div>

        <div style={{ flex: '1 1 480px', minWidth: 360, position: 'relative' }}>
          <div ref={mapContainerRef} data-testid={`${dataTestId}-map`} style={{ width: '100%', height: 560, borderRadius: 18, overflow: 'hidden', border: '1px solid #E7E7E7' }} />
        </div>
      </div>
    </div>
  );
};

export default AdminGeoMapExplorer;